import { FC, ReactNode, useState } from 'react'
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import HelpCenterOutlined from '@mui/icons-material/HelpCenterOutlined'
import Close from '@mui/icons-material/Close'
import LoadedIconButton from './LoadedIconButton'
import FullScreenSheet from './FullScreenSheet'

type LoadedHelpFlyoutProps = {
  /**
   * Content shown inside the flyout body. Pass a render function to receive a
   * `close` callback (e.g. to dismiss the flyout from a CTA inside the content).
   */
  children: ReactNode | ((close: () => void) => ReactNode)
  /** Tooltip shown on the trigger icon. */
  tooltipTitle?: string
  /** Padding around the flyout content. Defaults to 3 (24px). */
  contentPadding?: number | string
  /**
   * Fluid desktop drawer width. Defaults to a clamped value that grows with the
   * viewport but stays readable (floored so content never squeezes, capped so
   * text keeps a comfortable measure).
   */
  width?: string
}

/**
 * Help/instructions flyout: a HelpCenter trigger icon (meant to sit beside a
 * page title) that opens a responsive surface - a right slide-over Drawer on
 * desktop (page stays visible) and a full-screen sheet with a back button on
 * mobile (immersive).
 */
const LoadedHelpFlyout: FC<LoadedHelpFlyoutProps> = ({
  children,
  tooltipTitle = 'How to use this page',
  contentPadding = 3,
  // 560px floor keeps content from squeezing; 55vw scales with the viewport;
  // 900px cap stops lines growing past a comfortable reading measure on wide screens.
  width = 'clamp(560px, 55vw, 900px)',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('smxl'))
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const handleOpen = () => setIsOpen(true)
  const handleClose = () => setIsOpen(false)

  const body = typeof children === 'function' ? children(handleClose) : children
  const paddedBody = <Box sx={{ padding: contentPadding }}>{body}</Box>

  return (
    <>
      <LoadedIconButton
        Icon={HelpCenterOutlined}
        onClick={handleOpen}
        tooltipProps={{ title: tooltipTitle }}
      />

      {isMobile ? (
        <FullScreenSheet
          open={isOpen}
          onClose={handleClose}
          closeAriaLabel="Close instructions"
        >
          {paddedBody}
        </FullScreenSheet>
      ) : (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={handleClose}
          PaperProps={{ sx: { width } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ padding: 1, paddingLeft: 2 }}
          >
            <IconButton onClick={handleClose} aria-label="Close instructions">
              <Close />
            </IconButton>
          </Stack>
          <Divider />
          <Box flex={1} minHeight={0} overflow="auto">
            {paddedBody}
          </Box>
        </Drawer>
      )}
    </>
  )
}

export default LoadedHelpFlyout
